// Lecture de la FFBB (api.ffbb.app, Directus) DEPUIS L'APPAREIL.
//
// Pourquoi pas depuis le serveur : la FFBB répond 403 aux adresses de
// datacenter (constat Playbook du 2026-10-25), mais répond aux téléphones et
// aux ordinateurs, avec Access-Control-Allow-Origin: *. Pour rester une
// requête « simple » (sans préflight, que la FFBB ne gère pas pour les
// en-têtes), le jeton public passe dans l'adresse (access_token), jamais en
// en-tête. Aucun secret : ce jeton est distribué à tout client par
// /items/configuration.
import type { FfbbRencontre, Standing } from '../domain/ffbb';
import { competitionKind, standingsFrom, type CompetitionType } from '../domain/ffbb';
import { opponentOrganisme } from '../domain/derogation';
import { ORG_FIELDS, logoAssetUrl, opponentOrgObject } from '../domain/annuaire';

const BASE = 'https://api.ffbb.app/';
let token: { value: string; at: number } | null = null;

// --- Santé de la FFBB (D-113) : chaque échec est signalé (code HTTP, étape), sans donnée personnelle ----------
export type FfbbStep = 'jeton' | 'rencontres' | 'engagements' | 'organismes' | 'classement' | 'logo' | 'autre';
/** Un échec de lecture : le code HTTP (0 : pas de réponse, réseau ou CORS) et l'étape. */
export class FfbbError extends Error {
  constructor(readonly status: number, readonly step: FfbbStep) { super(`FFBB indisponible (${status || 'pas de réponse'})`); }
}
let reporter: ((status: number, step: FfbbStep) => void) | null = null;
/** Qui prévient la base d'un échec (installé au démarrage de l'app, voir ffbb-sync.ts). */
export const onFfbbFailure = (fn: ((status: number, step: FfbbStep) => void) | null) => { reporter = fn; };
const failure = (status: number, step: FfbbStep) => {
  try { reporter?.(status, step); } catch { /* un signalement ne gêne jamais la lecture */ }
  return new FfbbError(status, step);
};
async function call(url: string | URL, step: FfbbStep): Promise<Response> {
  let r: Response;
  try { r = await fetch(url); } catch { throw failure(0, step); }
  if (!r.ok) throw failure(r.status, step);
  return r;
}
const stepOf = (path: string): FfbbStep => /rencontres/.test(path) ? 'rencontres' : /engagements/.test(path) ? 'engagements'
  : /organismes/.test(path) ? 'organismes' : /poules/.test(path) ? 'classement' : 'autre';

async function publicToken(): Promise<string> {
  if (token && Date.now() - token.at < 3600_000) return token.value;
  const r = await call(`${BASE}items/configuration`, 'jeton');
  const j = await r.json().catch(() => null);
  const key = (j?.data ?? j)?.key_dh;
  if (!key) throw failure(r.status, 'jeton');   // la FFBB a changé sa configuration : à savoir vite
  token = { value: key, at: Date.now() };
  return key;
}

interface Query { filter?: unknown; fields?: string[]; sort?: string[]; limit?: number }

async function get<T>(path: string, q: Query = {}): Promise<T> {
  const url = new URL(BASE + path);
  if (q.filter) url.searchParams.set('filter', JSON.stringify(q.filter));
  q.fields?.forEach(f => url.searchParams.append('fields[]', f));
  q.sort?.forEach(s => url.searchParams.append('sort[]', s));
  if (q.limit) url.searchParams.set('limit', String(q.limit));
  url.searchParams.set('access_token', await publicToken());
  const r = await call(url, stepOf(path));
  return (await r.json()).data as T;
}

export interface Engagement {
  engagement_id: string;
  poule_id: string;
  poule_name: string;
  competition_name: string;
  competition_code: string | null;
  competition_type: CompetitionType;
  category: string | null;     // SE, U18, U15…
  sex: string | null;          // F, M
  team_name: string | null;
  team_number: number | null;   // numéro d'équipe FFBB (1, 2…) ; null : sans numéro (brassage, championnat unique)
}

/** Le club (organisme) de la FFBB par son code : nom, commune, comité (console, D-081). */
export async function clubOrganisme(clubCode: string): Promise<{ name: string; town: string | null; committee: string | null } | null> {
  const rows = await get<Record<string, any>[]>('items/ffbbserver_organismes', {
    filter: { code: { _eq: clubCode.trim().toUpperCase() } },
    fields: ['nom', 'code', 'commune.libelle', 'organisme_id_pere.nom'],
    limit: 1,
  });
  const o = rows[0];
  return o ? { name: String(o.nom ?? ''), town: o.commune?.libelle ?? null, committee: o.organisme_id_pere?.nom ?? null } : null;
}

/** La fiche complète d'un club par son code (l'équipe gratuite, D-166) : celle de l'annuaire (D-094), e-mail et logo compris. */
export async function clubOrganismeRow(clubCode: string): Promise<Record<string, any> | null> {
  const rows = await get<Record<string, any>[]>('items/ffbbserver_organismes', {
    filter: { code: { _eq: clubCode.trim().toUpperCase() } }, fields: ORG_FIELDS, limit: 1,
  });
  return rows[0] ?? null;
}

/** Engagements (équipes inscrites en compétition) d'un club, par son code FFBB. Le type vient de la FFBB
 *  (`typeCompetition` : DIV, COUPE, PLAT…), précisé par le nom (D-090). */
export async function clubEngagements(clubCode: string): Promise<Engagement[]> {
  const rows = await get<Record<string, any>[]>('items/ffbbserver_engagements', {
    filter: { idOrganisme: { code: { _eq: clubCode.trim().toUpperCase() } } },
    fields: ['id', 'nomEquipe', 'numeroEquipe', 'idPoule.id', 'idPoule.nom', 'idCompetition.nom', 'idCompetition.code', 'idCompetition.sexe', 'idCompetition.categorie.code', 'idCompetition.typeCompetition'],
    limit: 200,
  });
  return rows.filter(e => e.idPoule?.id).map(e => ({
    engagement_id: String(e.id),
    poule_id: String(e.idPoule.id),
    poule_name: e.idPoule.nom ?? '',
    competition_name: e.idCompetition?.nom ?? 'Compétition',
    competition_code: e.idCompetition?.code ?? null,
    competition_type: competitionKind(e.idCompetition?.typeCompetition, e.idCompetition?.nom ?? ''),
    category: e.idCompetition?.categorie?.code ?? null,
    sex: e.idCompetition?.sexe ?? null,
    team_name: e.nomEquipe ?? null,
    team_number: Number(e.numeroEquipe) > 0 ? Number(e.numeroEquipe) : null,
  }));
}

/** Le club adverse d'une de nos rencontres FFBB, et l'e-mail de sa fiche (pré-demande de
 *  dérogation, D-083) : l'organisme de l'AUTRE engagement de la rencontre. `org` : sa fiche complète,
 *  pour mettre l'annuaire des clubs à jour (D-094). */
export async function matchOpponentClub(ffbbMatchId: string, ourEngagement: string | null): Promise<{ name: string; email: string | null; org: Record<string, any> | null } | null> {
  const side = (n: 1 | 2) => [`idEngagementEquipe${n}.id`, ...ORG_FIELDS.map(f => `idEngagementEquipe${n}.idOrganisme.${f}`)];
  const rows = await get<Record<string, any>[]>('items/ffbbserver_rencontres', {
    filter: { id: { _eq: ffbbMatchId } }, fields: [...side(1), ...side(2)], limit: 1,
  });
  const o = opponentOrganisme(rows[0], ourEngagement);
  return o ? { ...o, org: opponentOrgObject(rows[0], ourEngagement) } : null;
}

/** Les fiches de ces organismes (annuaire des clubs, D-094) : un paquet, une requête. */
export const organismes = (ids: string[]) =>
  ids.length ? get<Record<string, any>[]>('items/ffbbserver_organismes', { filter: { id: { _in: ids } }, fields: ORG_FIELDS, limit: ids.length })
    : Promise.resolve([] as Record<string, any>[]);

/** Le logo d'un organisme, déjà réduit par la FFBB (~3 Ko) ; l'original (~50 Ko) si la réduction n'est pas
 *  servie. Null si rien ne vient. Servi avec Access-Control-Allow-Origin: * (relevé le 2026-10-28). */
export async function organismeLogo(ref: string): Promise<Blob | null> {
  const key = await publicToken();
  for (const reduced of [true, false]) {
    const r = await fetch(logoAssetUrl(BASE, ref, key, reduced)).catch(() => null);
    if (r?.ok && /^image\//.test(r.headers.get('content-type') ?? '')) return r.blob();
  }
  return null;
}

const RENC_FIELDS = [
  'id', 'date_rencontre', 'nomEquipe1', 'nomEquipe2',
  // Les engagements développés : l'organisme adverse de nos rencontres entre dans l'annuaire des clubs (D-094).
  'idEngagementEquipe1.id', 'idEngagementEquipe1.idOrganisme.id', 'idEngagementEquipe2.id', 'idEngagementEquipe2.idOrganisme.id',
  'resultatEquipe1', 'resultatEquipe2', 'joue', 'forfaitEquipe1', 'forfaitEquipe2', 'salle.libelle', 'salle.commune.libelle',
  // La salle géolocalisée (D-053) : le trajet des matchs à l'extérieur.
  'salle.adresse', 'salle.cartographie.latitude', 'salle.cartographie.longitude', 'salle.cartographie.codePostal', 'salle.cartographie.ville',
];

export const pouleRencontres = (pouleId: string) =>
  get<FfbbRencontre[]>('items/ffbbserver_rencontres', {
    filter: { idPoule: { _eq: Number(pouleId) } }, fields: RENC_FIELDS, sort: ['date_rencontre'], limit: 400,
  });

export async function pouleStandings(pouleId: string): Promise<Standing[]> {
  const data = await get<{ classements?: Record<string, unknown>[] } | null>(`items/ffbbserver_poules/${pouleId}`, {
    fields: ['classements.position', 'classements.points', 'classements.matchJoues', 'classements.gagnes', 'classements.perdus',
      'classements.nombreForfaits', 'classements.paniersMarques', 'classements.paniersEncaisses', 'classements.difference',
      'classements.organisme_nom', 'classements.horsClassement', 'classements.idEngagement'],
  });
  return standingsFrom(data?.classements);
}
